import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js'
import { useCartStore, selectTotalPrice } from '@store/useCartStore'
import { useToastStore } from '@store/useToastStore'
import { createOrder } from '@services/order.service'
import { createPaymentIntent } from '@services/payment.service'
import Spinner from '@components/UI/Spinner'
import './CheckoutPage.scss'

const FREE_SHIPPING_THRESHOLD = 150
const SHIPPING_COST = 15

const CARD_STYLE = {
  base: {
    fontSize: '15px',
    fontFamily: "'Jost', sans-serif",
    color: '#2C2018',
    '::placeholder': { color: '#9B8F87' },
  },
  invalid: { color: '#DC2626' },
}

const EMPTY_FORM = {
  email: '',
  first_name: '',
  last_name: '',
  phone: '',
  address: '',
  city: '',
  postal_code: '',
  county: '',
  notes: '',
}

export default function CheckoutPage() {
  const navigate = useNavigate()
  const stripe = useStripe()
  const elements = useElements()
  const items = useCartStore((s) => s.items)
  const clearCart = useCartStore((s) => s.clearCart)
  const subtotal = useCartStore(selectTotalPrice)
  const addToast = useToastStore((s) => s.addToast)

  const shipping = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_COST
  const total = Math.round((subtotal + shipping) * 100) / 100

  const [form, setForm] = useState(EMPTY_FORM)
  const [paymentMethod, setPaymentMethod] = useState('card')
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }))
  }

  const validate = () => {
    const e = {}
    if (!form.email.trim()) e.email = 'Email obligatoriu'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Email invalid'
    if (!form.first_name.trim()) e.first_name = 'Prenumele obligatoriu'
    if (!form.last_name.trim()) e.last_name = 'Numele obligatoriu'
    if (!form.phone.trim()) e.phone = 'Telefonul obligatoriu'
    if (!form.address.trim()) e.address = 'Adresa obligatorie'
    if (!form.city.trim()) e.city = 'Orașul obligatoriu'
    if (!form.postal_code.trim()) e.postal_code = 'Codul poștal obligatoriu'
    return e
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      addToast({ message: 'Completează toate câmpurile obligatorii', type: 'warning' })
      return
    }
    if (items.length === 0) {
      addToast({ message: 'Coșul tău este gol', type: 'warning' })
      return
    }

    setSubmitting(true)
    try {
      let stripePaymentIntentId = null

      if (paymentMethod === 'card') {
        if (!stripe || !elements) {
          addToast({ message: 'Stripe nu s-a încărcat. Reîncearcă.', type: 'error' })
          setSubmitting(false)
          return
        }

        const itemsPayload = items.map((i) => ({
          product_id: i.productId,
          product_name: i.productName,
          product_slug: i.productSlug,
          product_image: i.productImage,
          variant_name: i.variantName,
          quantity: i.quantity,
          unit_price: i.unitPrice,
        }))
        const piRes = await createPaymentIntent({
          items: itemsPayload,
          discount_amount: 0,
        })
        const { clientSecret, paymentIntentId } = piRes.data

        const { error: stripeError } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: `${form.first_name.trim()} ${form.last_name.trim()}`,
              email: form.email.trim(),
              phone: form.phone.trim(),
            },
          },
        })

        if (stripeError) {
          addToast({ message: stripeError.message || 'Plata a eșuat', type: 'error' })
          setSubmitting(false)
          return
        }

        stripePaymentIntentId = paymentIntentId
      }

      const orderPayload = {
        customer: {
          email: form.email.trim(),
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          phone: form.phone.trim(),
        },
        shipping: {
          address: form.address.trim(),
          city: form.city.trim(),
          postal_code: form.postal_code.trim(),
          county: form.county.trim() || undefined,
        },
        items: items.map((i) => ({
          product_id: i.productId,
          product_name: i.productName,
          product_slug: i.productSlug,
          product_image: i.productImage,
          variant_name: i.variantName,
          quantity: i.quantity,
          unit_price: i.unitPrice,
        })),
        payment_method: paymentMethod,
        customer_notes: form.notes.trim() || undefined,
        shipping_cost: shipping,
        ...(stripePaymentIntentId && { stripe_payment_intent_id: stripePaymentIntentId }),
      }

      const orderRes = await createOrder(orderPayload)
      const order = orderRes.data

      clearCart()
      navigate(`/order-success?id=${order.id}&number=${order.order_number}`)
    } catch (err) {
      const msg = err?.response?.data?.message || 'Ceva nu a mers. Încearcă din nou.'
      addToast({ message: msg, type: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  if (items.length === 0) {
    return (
      <div className="checkout-page">
        <div className="checkout-empty">
          <h1 className="checkout-title">Checkout</h1>
          <p>Coșul tău este gol.</p>
          <Link to="/shop" className="btn-primary">Descoperă produsele</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="checkout-page">
      <h1 className="checkout-title">Finalizare comandă</h1>

      <form className="checkout-layout" onSubmit={handleSubmit} noValidate>
        <div className="checkout-form">
          <fieldset className="checkout-section">
            <legend className="checkout-legend">Date personale</legend>
            <div className="checkout-row-2">
              <Field label="Prenume *" name="first_name" value={form.first_name} onChange={handleChange} error={errors.first_name} />
              <Field label="Nume *" name="last_name" value={form.last_name} onChange={handleChange} error={errors.last_name} />
            </div>
            <Field label="Email *" name="email" type="email" value={form.email} onChange={handleChange} error={errors.email} />
            <Field label="Telefon *" name="phone" type="tel" value={form.phone} onChange={handleChange} error={errors.phone} />
          </fieldset>

          <fieldset className="checkout-section">
            <legend className="checkout-legend">Adresă de livrare</legend>
            <Field label="Adresă *" name="address" value={form.address} onChange={handleChange} error={errors.address} />
            <div className="checkout-row-2">
              <Field label="Oraș *" name="city" value={form.city} onChange={handleChange} error={errors.city} />
              <Field label="Cod poștal *" name="postal_code" value={form.postal_code} onChange={handleChange} error={errors.postal_code} />
            </div>
            <Field label="Județ" name="county" value={form.county} onChange={handleChange} />
          </fieldset>

          <fieldset className="checkout-section">
            <legend className="checkout-legend">Notă comandă</legend>
            <textarea
              className="checkout-textarea"
              name="notes"
              value={form.notes}
              onChange={handleChange}
              placeholder="Instrucțiuni speciale (opțional)"
              rows={3}
            />
          </fieldset>

          <fieldset className="checkout-section">
            <legend className="checkout-legend">Metodă de plată</legend>
            <div className="checkout-payment-options">
              <label className={`checkout-payment-opt ${paymentMethod === 'card' ? 'active' : ''}`}>
                <input type="radio" name="payment_method" value="card" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} />
                <span className="checkout-payment-label">Card bancar</span>
              </label>
              <label className={`checkout-payment-opt ${paymentMethod === 'cash_on_delivery' ? 'active' : ''}`}>
                <input type="radio" name="payment_method" value="cash_on_delivery" checked={paymentMethod === 'cash_on_delivery'} onChange={() => setPaymentMethod('cash_on_delivery')} />
                <span className="checkout-payment-label">Ramburs la livrare</span>
              </label>
            </div>

            {paymentMethod === 'card' && (
              <div className="checkout-card-element">
                <CardElement options={{ style: CARD_STYLE, hidePostalCode: true }} />
              </div>
            )}
          </fieldset>
        </div>

        <aside className="checkout-sidebar">
          <h2 className="checkout-sidebar-title">Comanda ta</h2>

          <div className="checkout-order-items">
            {items.map((item) => {
              const key = `${item.productId}-${item.variantName ?? 'default'}`
              return (
                <div key={key} className="checkout-order-item">
                  <div className="checkout-order-item-info">
                    <span className="checkout-order-item-name">{item.productName}</span>
                    {item.variantName && <span className="checkout-order-item-variant">{item.variantName}</span>}
                    <span className="checkout-order-item-qty">× {item.quantity}</span>
                  </div>
                  <span className="checkout-order-item-price">
                    {Math.round(item.unitPrice * item.quantity * 100) / 100} lei
                  </span>
                </div>
              )
            })}
          </div>

          <div className="checkout-summary-divider" />

          <div className="checkout-summary-row">
            <span>Subtotal</span>
            <span>{Math.round(subtotal * 100) / 100} lei</span>
          </div>
          <div className="checkout-summary-row">
            <span>Livrare</span>
            <span>{shipping === 0 ? 'Gratuită' : `${shipping} lei`}</span>
          </div>
          <div className="checkout-summary-divider" />
          <div className="checkout-summary-row checkout-summary-total">
            <span>Total</span>
            <span>{total} lei</span>
          </div>

          <button type="submit" className="checkout-submit-btn" disabled={submitting}>
            {submitting ? (
              <span className="checkout-submit-loading"><Spinner size={18} /> Se procesează…</span>
            ) : (
              paymentMethod === 'card' ? 'Plătește acum' : 'Plasează comanda'
            )}
          </button>
        </aside>
      </form>
    </div>
  )
}

function Field({ label, name, type = 'text', value, onChange, error }) {
  return (
    <div className={`checkout-field ${error ? 'has-error' : ''}`}>
      <label className="checkout-label" htmlFor={`co-${name}`}>{label}</label>
      <input
        id={`co-${name}`}
        className="checkout-input"
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        autoComplete={name.replace('_', '-')}
      />
      {error && <span className="checkout-error">{error}</span>}
    </div>
  )
}
